/**
 * Tour and hint definitions — ALL copy lives here and nowhere else.
 *
 * Steps are keyed by role (TOUR_STEPS_BY_ROLE). A step with a `target` is
 * highlighted via `[data-tour="<target>"]`; a null target is a centered
 * popover. `navState` is the sidebar choreography the provider applies
 * before the step highlights: drawer 'open' (nav open at every width, group
 * expanded) or 'closed' (the phone drawer closed so it doesn't overlay the
 * content — desktop leaves the rail alone).
 *
 * Copy status (owner + Vesile edit at PR review): TOUR_VERSION 1. The
 * 'settings' step joined in v3.87.0 without a version bump.
 */
import {
  TOUR_TARGETS,
  HINT_KEYS,
  NAV_GROUP_LETTER_SOUND,
  NAV_GROUP_MANAGEMENT,
  NAV_GROUP_HELP,
  FIRST_ASSESSMENT_VIDEO_ID,
} from './constants';
import { formatDuration } from './helpVideos';

const NAV_OPEN_LETTER_SOUND = Object.freeze({ drawer: 'open', expandGroup: NAV_GROUP_LETTER_SOUND });
const NAV_OPEN_MANAGEMENT = Object.freeze({ drawer: 'open', expandGroup: NAV_GROUP_MANAGEMENT });
const NAV_OPEN_HELP = Object.freeze({ drawer: 'open', expandGroup: NAV_GROUP_HELP });
const NAV_CLOSED = Object.freeze({ drawer: 'closed' });

export function buildTeacherTourSteps({ firstName = null, firstAssessmentVideo = null } = {}) {
  const watch = firstAssessmentVideo
    ? {
        label: `Watch: ${firstAssessmentVideo.title}${
          formatDuration(firstAssessmentVideo.durationSec) ? ` · ${formatDuration(firstAssessmentVideo.durationSec)}` : ''
        }`,
        video: firstAssessmentVideo,
      }
    : null;

  return [
    {
      id: 'welcome',
      target: null,
      navState: NAV_CLOSED,
      title: firstName ? `Welcome, ${firstName}` : 'Welcome to Literacy Assessments',
      body: "Here's a 60-second tour of where things are. Skip it if you like — you can replay it any time from Help.",
      nextLabel: 'Start tour',
      showProgress: false,
    },
    {
      id: 'classes',
      target: TOUR_TARGETS.classFilter,
      navState: NAV_CLOSED,
      side: 'bottom',
      title: 'Your classes',
      body: "Your classes and students come from your district's system through Clever. If a name or a roster looks off, tell your school office — it updates here on the next sync.",
      showProgress: true,
    },
    {
      id: 'nav',
      target: TOUR_TARGETS.navLetterSound,
      navState: NAV_OPEN_LETTER_SOUND,
      side: 'right',
      title: 'Assessments live in the left menu',
      body: 'Each section has three pages: Assessment (give one), Results (scores by class and student), and Small Groups (grouping suggestions built from those results).',
      showProgress: true,
    },
    {
      id: 'bands',
      target: TOUR_TARGETS.bandSummary,
      navState: NAV_CLOSED,
      side: 'top',
      title: 'Your class at a glance',
      body: "Once you've assessed, each class card sorts students into Needs Support, Emerging, and Mastery for the test you pick above.",
      showProgress: true,
    },
    {
      // v3.87.0 — where the tests are fitted to the class. Added without a
      // TOUR_VERSION bump: teachers who have seen the tour are not shown it
      // again; they meet the setting on the test page itself.
      id: 'settings',
      target: TOUR_TARGETS.navManagement,
      navState: NAV_OPEN_MANAGEMENT,
      side: 'right',
      title: 'Make the tests fit your class',
      body: 'Assessment Settings is where you choose the letter order, the word lists, capital or lowercase words, and which sections show in this menu.',
      showProgress: true,
    },
    {
      id: 'help',
      target: TOUR_TARGETS.navHelp,
      navState: NAV_OPEN_HELP,
      side: 'right',
      title: 'Help is here',
      body: 'Short how-to videos, what’s new, this tour again, and how to reach us.',
      showProgress: true,
    },
    {
      id: 'ready',
      target: null,
      navState: NAV_CLOSED,
      title: 'Ready to give your first assessment?',
      body: 'Open Letter/Sound → Assessment, pick a student, and follow the prompts on the card.',
      nextLabel: 'Done',
      secondary: watch,
      showProgress: false,
    },
  ];
}

export const TOUR_STEPS_BY_ROLE = Object.freeze({
  teacher: buildTeacherTourSteps,
});

/** Steps for a role; unknown roles (replay by TLS/admin) get the teacher tour. */
export function buildTourSteps(role, opts) {
  const build = TOUR_STEPS_BY_ROLE[role] || TOUR_STEPS_BY_ROLE.teacher;
  return build(opts);
}

export const HINTS = Object.freeze({
  [HINT_KEYS.assessLetterSound]: Object.freeze({
    key: HINT_KEYS.assessLetterSound,
    target: TOUR_TARGETS.assessControls,
    side: 'top',
    title: 'Scoring as you go',
    body: '→ marks correct, ← marks incorrect. Or type the letter the student actually said — it’s marked incorrect and their answer is recorded, so Results can show it.',
    swallowAssessmentKeys: true,
  }),
  [HINT_KEYS.results]: Object.freeze({
    key: HINT_KEYS.results,
    target: TOUR_TARGETS.resultsFirstTable,
    side: 'bottom',
    title: 'Reading your results',
    body: 'Colors show how each student is doing. Click a student’s name to see their answers. In Detailed Letter Responses below, a red cell shows the letter the student said instead.',
    swallowAssessmentKeys: false,
  }),
});

export const HINT_DISMISS_LABEL = 'Got it';
export const TOUR_SKIP_LABEL = 'Skip';
export const TOUR_BACK_LABEL = 'Back';
export const TOUR_NEXT_LABEL = 'Next';

export const targetSelector = (target) => `[data-tour="${target}"]`;

export function isTargetPresent(target, root = typeof document !== 'undefined' ? document : null) {
  if (!target) return true;
  if (!root) return false;
  return !!root.querySelector(targetSelector(target));
}

/** Drop steps whose target is missing from the DOM (untargeted steps stay). */
export function filterStepsToPresentTargets(steps, root) {
  return steps.filter((s) => !s.target || isTargetPresent(s.target, root));
}

export function countTargetedSteps(steps) {
  return steps.filter((s) => !!s.target).length;
}

/** The last-step video: the enabled helpVideos entry with the pinned id. */
export function findFirstAssessmentVideo(videos) {
  if (!Array.isArray(videos)) return null;
  return videos.find((v) => v && v.id === FIRST_ASSESSMENT_VIDEO_ID) || null;
}
